import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ProjectContextProvider } from './contexts/ProjectContext';
import { GlobalProjectProvider } from './contexts/GlobalProjectContext';
import { UserProvider } from './contexts/UserContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppThemeProvider } from './contexts/AppThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ToastProvider } from './components/ui/toast';
import RequireAuth from './components/auth/RequireAuth';
import InDevelopmentRouteGuard from './components/auth/InDevelopmentRouteGuard';
import LoginPage from './pages/auth/LoginPage';
import CallbackPage from './pages/auth/CallbackPage';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import ProjectUploadWizard from './pages/projects/ProjectUploadWizard';
import ProjectDetail from './pages/projects/ProjectDetail';
import ProjectActivities from './pages/projects/ProjectActivities';
import ProjectResources from './pages/projects/ProjectResources';
import ProjectWBS from './pages/projects/ProjectWBS';
import ProjectReports from './pages/projects/ProjectReports';
import ProjectInsights from './pages/projects/ProjectInsights';
import ProjectDashboard from './pages/projects/ProjectDashboard';
import { 
  Reports, 
  Insights, 
  Analytics,
  Activities, 
  Projects, 
  Schedules, 
  XERPage, 
  NotFound,
  Unauthorized,
} from './pages';
import Explorer from './pages/Explorer';
// import ReportViewer from './pages/reports/ReportViewer'; // Replaced with new Reports system
import Templates from './pages/management/Templates';
import Archive from './pages/management/Archive';
import ScheduleDetail from './pages/schedules/ScheduleDetail';
import ScheduleComparison from './pages/schedules/ScheduleComparison';
import ProjectLoader from './components/ProjectLoader';
import useEnvironment from './hooks/useEnvironment';
// Import admin pages
import UserManagement from './pages/admin/UserManagement';
import OrganizationManagement from './pages/admin/OrganizationManagement';
import SystemSettings from './pages/admin/SystemSettings';
// Import activity logs page
import ActivityLogs from './pages/management/ActivityLogs';
// Import settings page
import Settings from './pages/Settings';
import { TimelineOfTruth, ChangeIntelligence, CompileGate, Forecast, DecisionPack } from './pages/project-state';
import { BaselineSelectorProvider } from './contexts/BaselineSelectorContext';
import BaselinesPage from './pages/baselines/BaselinesPage';
import GanttPage from './pages/GanttPage';
import { AppErrorBoundary } from './components/errors/AppErrorBoundary';
import { RouteErrorBoundary } from './components/errors/RouteErrorBoundary';
import { SESSION_TOKEN_KEY } from './api/workosAuth';

/** Read session token from redirect fragment (backend sends app/#session=TOKEN when cookie may be blocked). */
function useSessionFragmentHandoff() {
  // Store token synchronously on first run so it exists before any child component's effect runs
  if (typeof window !== 'undefined') {
    const hash = window.location.hash;
    if (hash.startsWith('#session=')) {
      try {
        const token = decodeURIComponent(hash.slice(9).trim());
        if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token);
      } catch {
        // ignore
      }
    }
  }
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (hash.startsWith('#session=')) {
      try {
        const token = decodeURIComponent(hash.slice(9).trim());
        if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token);
      } catch {
        // ignore
      }
    }
  }, []);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash.startsWith('#session=')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);
}

// Create a client
const queryClient = new QueryClient();

/** Sends PostHog pageview on route change so you get time-on-page and page analytics. */
function PostHogPageView() {
  const location = useLocation();
  const posthog = usePostHog();
  useEffect(() => {
    if (posthog && location.pathname) {
      posthog.capture('$pageview', { $current_url: window.location.href });
    }
  }, [location.pathname, location.search, posthog]);
  return null;
}

const App: React.FC = () => {
  const { useMockData, isDevelopment } = useEnvironment();
  useSessionFragmentHandoff();

  // Log mock data status on startup (dev only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('==================================');
      console.log(`🔍 MOCK DATA: ${useMockData ? 'ENABLED' : 'DISABLED'}`);
      console.log(`🌐 ENVIRONMENT: ${isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'}`);
      console.log('==================================');
      if (useMockData) {
        console.warn('⚠️ Using mock data - for development only!');
        console.warn('To disable mock data, run: npm run toggle-mock');
      }
    }
  }, [useMockData, isDevelopment]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeProvider defaultTheme="dark">
        <UserProvider>
          <SettingsProvider>
            <NotificationProvider>
              <ToastProvider>
                <ThemeProvider>
                  <Router>
                  <AuthProvider>
                  <GlobalProjectProvider>
                    <ProjectContextProvider>
                <BaselineSelectorProvider>
                <AppErrorBoundary>
                <PostHogPageView />
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/callback" element={<CallbackPage />} />
                  <Route element={<RequireAuth><></></RequireAuth>}>
                    <Route path="/" element={<RouteErrorBoundary><Dashboard /></RouteErrorBoundary>} />
                    <Route path="/app/home" element={<Home />} />
                    <Route path="/app/upload" element={<ProjectUploadWizard />} />
                    <Route path="/app/projects" element={<Projects />} />
                    <Route path="/app/activities" element={<RouteErrorBoundary><Activities /></RouteErrorBoundary>} />
                    <Route path="/app/insights" element={<InDevelopmentRouteGuard><Insights /></InDevelopmentRouteGuard>} />
                    <Route path="/app/gantt" element={<InDevelopmentRouteGuard><GanttPage /></InDevelopmentRouteGuard>} />
                    <Route path="/app/analytics/*" element={<RouteErrorBoundary><Analytics /></RouteErrorBoundary>} />
                    <Route path="/app/baselines" element={<BaselinesPage />} />
                    <Route path="/app/explorer" element={<Explorer />} />
                    <Route path="/app/reports/*" element={<RouteErrorBoundary><Reports /></RouteErrorBoundary>} />
                    <Route path="/app/settings" element={<Settings />} />
                    <Route path="/app/projects/:projectId" element={<ProjectDetail />} />
                    <Route path="/app/projects/:projectId/activities" element={<ProjectActivities />} />
                    <Route path="/app/projects/:projectId/resources" element={<ProjectResources />} />
                    <Route path="/app/projects/:projectId/wbs" element={<ProjectWBS />} />
                    <Route path="/app/projects/:projectId/reports" element={<ProjectReports />} />
                    <Route path="/app/projects/:projectId/insights" element={<ProjectInsights />} />
                    <Route path="/app/dashboard/:projectId" element={<ProjectDashboard />} />
                    <Route path="/app/project-state/:projectId/timeline" element={<InDevelopmentRouteGuard><TimelineOfTruth /></InDevelopmentRouteGuard>} />
                    <Route path="/app/project-state/:projectId/change-intelligence" element={<InDevelopmentRouteGuard><ChangeIntelligence /></InDevelopmentRouteGuard>} />
                    <Route path="/app/project-state/:projectId/compile-gate" element={<InDevelopmentRouteGuard><CompileGate /></InDevelopmentRouteGuard>} />
                    <Route path="/app/project-state/:projectId/forecast" element={<InDevelopmentRouteGuard><Forecast /></InDevelopmentRouteGuard>} />
                    <Route path="/app/project-state/:projectId/decision-pack" element={<InDevelopmentRouteGuard><DecisionPack /></InDevelopmentRouteGuard>} />
                    <Route path="/app/xer/project/:projectId" element={<ProjectDetail />} />
                    <Route path="/app/xer/project/:projectId/activities" element={<ProjectDetail />} />
                    <Route path="/app/xer/project/:projectId/gantt" element={<ProjectDetail />} />
                    <Route path="/app/xer/project/:projectId/resources" element={<ProjectDetail />} />
                    <Route path="/app/xer/project/:projectId/wbs" element={<ProjectDetail />} />
                    <Route path="/app/xer/project/:projectId/reports" element={<ProjectDetail />} />
                    <Route path="/app/xer/project/:projectId/insights" element={<ProjectDetail />} />
                    <Route path="/app/xer/project/:projectId/settings" element={<ProjectDetail />} />
                    <Route path="/app/xer" element={<XERPage />} />
                    <Route path="/app/legacy/projects/:projectId/schedules" element={<Schedules />} />
                    <Route path="/app/legacy/projects/:projectId/schedules/:scheduleId" element={<ScheduleDetail />} />
                    <Route path="/app/legacy/projects/:projectId/schedules/compare" element={<ScheduleComparison />} />
                    <Route path="/app/templates" element={<Templates />} />
                    <Route path="/app/archive" element={<Archive />} />
                    <Route path="/app/activity" element={<ActivityLogs />} />
                    <Route path="/app/admin/users" element={<UserManagement />} />
                    <Route path="/app/admin/organizations" element={<OrganizationManagement />} />
                    <Route path="/app/admin/settings" element={<SystemSettings />} />
                  </Route>
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </AppErrorBoundary>
                </BaselineSelectorProvider>
                    </ProjectContextProvider>
                  </GlobalProjectProvider>
                </AuthProvider>
                  </Router>
                </ThemeProvider>
              </ToastProvider>
            </NotificationProvider>
          </SettingsProvider>
        </UserProvider>
      </AppThemeProvider>
      </QueryClientProvider>
  );
};

export default App;

